import {
  Button,
  Card,
  CardHeader,
  CardMedia,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { isDistanceInRepExercise } from "@readyfit-common/models";
import { useState } from "react";
import useStepMetadata from "src/grading/hooks/useStepMetadata";
import { didPassExercise } from "src/grading/utils/testHelpers";
import { useAppSelector } from "src/store/hooks";
import { useLazyGetFileUrlQuery } from "src/store/reducers/documents/documentsApiSlice";
import { selectGradingStep } from "src/store/reducers/grading/gradingSlice";
import { TestScore } from "src/store/reducers/scores/scoresApiSlice";
import { ApprovalGradeResult } from "../../models";
import useStyles from "./ReviewApprovalCard.styles";

type ReviewApprovalCardProps = ApprovalGradeResult & {
  regradeClick: () => void;
  fileKey: string | undefined;
  step: number;
  testScore: TestScore;
};

export function ReviewApprovalCard({
  userResult,
  discrepancies,
  fileKey,
  notes,
  regradeClick,
  step,
  accuracyScore,
  testScore,
  approved,
}: ReviewApprovalCardProps) {
  const [videoUrl, setVideoUrl] = useState<URL | "pending" | "not_started">(
    "not_started",
  );
  const userScore = userResult.reps;
  const { stepMetadata } = useStepMetadata(step);
  const maximunValue = Number(
    stepMetadata?.testing?.userEnteredResult?.maximumValue ?? 100,
  );
  const gradingStep = useAppSelector(selectGradingStep);
  // const [isResultStepper, setIsResultStepper] = useState(false);
  const classes = useStyles();
  const [getFileUrl] = useLazyGetFileUrlQuery();

  if (videoUrl === "not_started") {
    setVideoUrl("pending");
    getFileUrl(`${fileKey}?ext=mp4`)
      .unwrap()
      .then((data) => setVideoUrl(new URL(data.url)))
      .catch(() => {
        console.error("Couldn't get video file");
      });
  }

  const isResultStepper = gradingStep > (testScore?.exercises?.length ?? 0);

  const grade = testScore?.exercises[step].grade;

  const isBodyComp =
    testScore?.exercises[step].metadata.exerciseType === "bodyComp";

  return (
    <Grid container spacing={2} xs={12}>
      <Card className={classes.gradingReviewCard}>
        <CardHeader
          className={classes.cardHeader}
          title={
            <Grid item container xs={12} alignItems={"center"}>
              <Typography
                variant="subtitle1"
                className={classes.stepNumberContainer}
              >
                {step}
              </Typography>
              <Typography variant="h4">{userResult.name}</Typography>

              <Typography
                variant="button"
                className={
                  didPassExercise(grade || "")
                    ? classes.approveLabel
                    : classes.failedLabel
                }
              >
                {grade}
              </Typography>

              {!isResultStepper && (
                <Grid item>
                  <Button
                    onClick={regradeClick}
                    className={classes.regradeButton}
                  >
                    Re-grade
                  </Button>
                </Grid>
              )}
            </Grid>
          }
        />
        <Grid container xs={12} px={4} py={3} gap={1}>
          {!isBodyComp && (
            <Grid
              item
              xs={4}
              className={classes.gradingReviewCardVideoContainer}
            >
              <CardMedia
                component="video"
                image={
                  videoUrl !== "pending" && videoUrl !== "not_started"
                    ? videoUrl.toString()
                    : undefined
                }
                className={classes.gradingReviewCardVideo}
                controls
              />
            </Grid>
          )}
          <Grid item flexDirection={"column"} marginLeft={"auto"}>
            {testScore?.exercises[step].score.toString().trim() !== "-" &&
              testScore?.exercises[step].score.toString().trim() !== "" && (
                <>
                  <Typography variant="subtitle1">Total Score</Typography>
                  <Typography variant="h1">
                    {testScore?.exercises[step].score}
                    <span style={{ color: "#8F8F8F" }}>
                      /{testScore?.exercises[step].maxScore}
                    </span>
                  </Typography>
                </>
              )}
          </Grid>

          <Grid xs={11}>
            <InputLabel>
              <Typography variant="subtitle1">Grader Notes</Typography>
            </InputLabel>
            <TextField
              fullWidth
              multiline
              variant="outlined"
              value={userResult?.graderNotes || "N/A"}
              disabled={true}
              placeholder="Grader notes about exercise"
            />
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
}
